/**
 * Karusell-innstillinger
 *
 * Velger hvilke dashboard som roterer i TV-modus, rekkefølgen og visningstiden for hvert.
 * Lagres i dashboardoppsettet (localStorage), ikke i databasen.
 */
import React from 'react';
import { X, Layers, ChevronUp, ChevronDown, Play } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { useDashboard } from '../DashboardContext';
import { useTVMode } from '../contexts/TVModeContext';
import { ClampedIntegerInput } from '../../../../common/inputs';
import { carouselSeconds, DEFAULT_CAROUSEL_SECONDS, MAX_CAROUSEL_SECONDS, MIN_CAROUSEL_SECONDS } from '../utils/refreshSettings';
import type { DashboardLayout } from '../types';

interface CarouselSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Alle dashboardene i karusellrekkefølge (også de som ikke er med). */
function orderedLayouts(layouts: DashboardLayout[]): DashboardLayout[] {
  return layouts
    .map((layout, index) => ({ layout, index }))
    .sort((a, b) => (a.layout.carousel?.order ?? a.index) - (b.layout.carousel?.order ?? b.index))
    .map(({ layout }) => layout);
}

const CarouselSettings: React.FC<CarouselSettingsProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { state, updateLayoutSettings } = useDashboard();
  const tvMode = useTVMode();

  if (!isOpen) return null;

  const layouts = orderedLayouts(state.availableLayouts);
  const included = layouts.filter((l) => l.carousel?.include !== false);
  const totalSeconds = included.reduce((sum, l) => sum + carouselSeconds(l), 0);

  const update = (layout: DashboardLayout, patch: Partial<NonNullable<DashboardLayout['carousel']>>) =>
    updateLayoutSettings(layout.id, { carousel: { ...layout.carousel, ...patch } });

  // Flytter et dashboard opp/ned: skriver fast rekkefølge på alle, så den ikke avhenger av listerekkefølgen
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= layouts.length) return;
    const reordered = [...layouts];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    reordered.forEach((layout, order) => {
      if (layout.carousel?.order !== order) update(layout, { order });
    });
  };

  const iconButton = 'flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-xl shadow-2xl border border-gray-700 w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Layers className="w-6 h-6 text-blue-400" />
            <div>
              <h2 className="text-xl font-semibold text-white">{t('manager.modules.wmsDashboard.carousel.title', {}, 'Karusell')}</h2>
              <p className="text-xs text-gray-400">
                {t('manager.modules.wmsDashboard.carousel.subtitle', {}, 'Dashboardene som er med, vises etter hverandre i TV-modus.')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={iconButton}
            aria-label={t('common.buttons.close', {}, 'Lukk')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {layouts.map((layout, index) => {
            const include = layout.carousel?.include !== false;
            return (
              <div
                key={layout.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${include ? 'bg-gray-800 border-gray-700' : 'bg-gray-900 border-gray-800 opacity-70'}`}
              >
                <label className="flex items-center gap-3 flex-1 min-w-0 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={include}
                    onChange={(e) => update(layout, { include: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 flex-shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-white truncate">{layout.name}</span>
                    <span className="block text-xs text-gray-400">
                      {t('manager.modules.wmsDashboard.carousel.widgetCount', { '0': layout.widgets.length }, `${layout.widgets.length} widgets`)}
                    </span>
                  </span>
                </label>

                <label className="flex items-center gap-2 text-xs text-gray-400 flex-shrink-0">
                  {t('manager.modules.wmsDashboard.carousel.seconds', {}, 'Sekunder')}
                  <ClampedIntegerInput
                    value={carouselSeconds(layout)}
                    onValueChange={(value) => update(layout, { seconds: value })}
                    min={MIN_CAROUSEL_SECONDS}
                    max={MAX_CAROUSEL_SECONDS}
                    disabled={!include}
                    className="w-20 min-h-[44px] px-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white text-right disabled:opacity-50"
                    aria-label={t('manager.modules.wmsDashboard.carousel.secondsFor', { '0': layout.name }, `Visningstid for ${layout.name} i sekunder`)}
                  />
                </label>

                <div className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className={iconButton}
                    aria-label={t('manager.modules.wmsDashboard.carousel.moveUp', {}, 'Flytt opp')}
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === layouts.length - 1}
                    className={iconButton}
                    aria-label={t('manager.modules.wmsDashboard.carousel.moveDown', {}, 'Flytt ned')}
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-700">
          <p className="text-xs text-gray-400">
            {included.length < 2
              ? t('manager.modules.wmsDashboard.carousel.needTwo', {}, 'Velg minst to dashboard for at karusellen skal rotere.')
              : t('manager.modules.wmsDashboard.carousel.summary', { '0': included.length, '1': totalSeconds },
                  `${included.length} dashboard, én runde tar ${totalSeconds} sekunder. Mellomrom pauser, Esc avslutter.`)}
            {' '}
            {t('manager.modules.wmsDashboard.carousel.defaultHint', { '0': DEFAULT_CAROUSEL_SECONDS }, `Standard visningstid er ${DEFAULT_CAROUSEL_SECONDS} sekunder.`)}
          </p>
          <button
            onClick={() => { onClose(); tvMode.enterTVMode(); }}
            disabled={included.length === 0}
            className="flex items-center gap-2 min-h-[44px] px-4 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <Play className="w-4 h-4" />
            {t('manager.modules.wmsDashboard.carousel.start', {}, 'Start karusell')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarouselSettings;
